import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';

@Component({
    selector: 'gtw-review-diagnostics-modal',
    templateUrl: './review-diagnostics.component.html',
    styleUrls: ['./review-diagnostics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReviewDiagnosticsComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() params: any = {};
  @Input() currentTheme = 'theme-black';

  @Output() dismissed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<boolean>();
  @Output() navigateToDiagnostics = new EventEmitter<any>();

  @ViewChild('ceHost', { read: ElementRef, static: false })
  ceHostRef?: ElementRef<HTMLElement>;

  ignored = false;
  dataLoaded = false;
  showBulkMessage = false;

  private ceElement: HTMLElement | null = null;
  private eventListeners: (() => void)[] = [];
  private ceLookupTimer: any = null;

  // Bounded retry: 20 * 100ms = 2s window for the legacy CE to mount.
  private static readonly CE_LOOKUP_MAX_ATTEMPTS = 20;
  private static readonly CE_LOOKUP_INTERVAL_MS = 100;

  constructor(
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible']) {
      return;
    }
    if (this.visible) {
      this.ignored = false;
      this.dataLoaded = false;
      this.showBulkMessage = false;
      this.setupCEListeners();
    } else {
      // Mirror legacy `$scope.$on('$destroy')` cleanup
      // (formViewCtrl.js L1802-1806) so reopening the modal starts clean.
      this.cancelCELookup();
      this.removeCEListeners();
      this.ignored = false;
      this.dataLoaded = false;
      this.showBulkMessage = false;
    }
  }

  ngOnDestroy(): void {
    this.cancelCELookup();
    this.removeCEListeners();
  }

  private setupCEListeners(): void {
    this.cancelCELookup();
    this.removeCEListeners();

    let attempts = 0;
    const tryAttach = () => {
      const ce = this.findCEElement();
      if (ce) {
        this.attachCEListeners(ce);
        return;
      }
      attempts++;
      if (attempts >= ReviewDiagnosticsComponent.CE_LOOKUP_MAX_ATTEMPTS) {
        return;
      }
      this.ceLookupTimer = setTimeout(
        tryAttach,
        ReviewDiagnosticsComponent.CE_LOOKUP_INTERVAL_MS
      );
    };

    // Defer first attempt one tick so the ViewChild query updates after the
    // *ngIf="visible" overlay renders.
    this.ceLookupTimer = setTimeout(
      tryAttach,
      ReviewDiagnosticsComponent.CE_LOOKUP_INTERVAL_MS
    );
  }

  private findCEElement(): HTMLElement | null {
    // Prefer the ViewChild (scoped to this component instance).
    const fromViewChild = this.ceHostRef?.nativeElement || null;
    if (fromViewChild) {
      return fromViewChild;
    }
    // Fall back to a host-scoped query so we never grab a stale CE that may
    // exist elsewhere in the DOM (e.g. another modal, lingering snapshot).
    const hostEl = this.host?.nativeElement;
    if (hostEl) {
      return hostEl.querySelector('gtw-pdf-review-diagnostics-ce') as HTMLElement | null;
    }
    return null;
  }

  private attachCEListeners(ce: HTMLElement): void {
    this.ceElement = ce;

    const onCancel = () => {
      this.completed.emit(this.ignored);
    };
    const onDataLoaded = (event: any) => {
      this.dataLoaded = true;
      const hasDiagnostics = event?.detail;
      if (!hasDiagnostics) {
        this.completed.emit(false);
      } else {
        this.showBulkMessage = !!(this.params?.submission_id?.includes(',') && hasDiagnostics);
      }
      this.cdr.detectChanges();
    };
    const onIgnore = () => {
      this.ignored = true;
      this.cdr.detectChanges();
    };

    // The CE (`gtw-pdf-review-diagnostics-ce`, registered via Angular
    // Elements) fires its `@Output('on-cancel' | 'on-data-loaded' |
    // 'on-ignore')` decorators as plain DOM events with those exact names.
    //
    // Legacy AngularJS bridged those into `$rootScope` events with a
    // `review:` prefix via the `reviewDiagEvents` directive
    // (formViewDirectives.js L133-L150) — the wrapper template still carries
    // the now-orphan `review-diag-events` attribute as documentation. The
    // migrated path has no such bridge, so we listen for the CE's native
    // event names directly. Without this, `dataLoaded` never flipped to
    // true and the resolve-by-link / bulk-message labels stayed hidden.
    ce.addEventListener('on-cancel', onCancel);
    ce.addEventListener('on-data-loaded', onDataLoaded);
    ce.addEventListener('on-ignore', onIgnore);

    this.eventListeners.push(
      () => ce.removeEventListener('on-cancel', onCancel),
      () => ce.removeEventListener('on-data-loaded', onDataLoaded),
      () => ce.removeEventListener('on-ignore', onIgnore)
    );
  }

  private cancelCELookup(): void {
    if (this.ceLookupTimer != null) {
      clearTimeout(this.ceLookupTimer);
      this.ceLookupTimer = null;
    }
  }

  private removeCEListeners(): void {
    this.eventListeners.forEach(cleanup => cleanup());
    this.eventListeners = [];
    this.ceElement = null;
  }

  dismiss(): void {
    this.dismissed.emit();
  }

  close(): void {
    this.completed.emit(this.ignored);
  }

  goToDiagnostics(ev?: Event): void {
    ev?.preventDefault();
    this.navigateToDiagnostics.emit({
      activity_key: '7289',
      screen_key: '11148',
      filters: [
        { filter_key: '1', name: 'tax_year', value: this.params?.tax_year },
        { filter_key: '2305', name: 'submission_id', value: this.params?.submission_id },
        { filter_key: '2', name: 'scenario', value: this.params?.scenario },
        { filter_key: '3', name: 'client_key', value: this.params?.client_key }
      ]
    });
  }

  get paramsJson(): string {
    return JSON.stringify(this.params || {});
  }
}
